// src/components/Gacha.js
import React, { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GameRulesButton } from './GameRules';
import SponsorSpot from './SponsorSpot';

import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';

// ★ SSR 以上は光る演出をする。
//   以前は SSR だけを固定で見ていたので、UR / LEGEND を追加しても
//   光らない状態だった。
//   ※ import はすべてファイル先頭にまとめる必要がある（import/first）。
const GLOW_RARITIES = ['SSR', 'UR', 'LEGEND'];
const isGlow = (card) => GLOW_RARITIES.includes(card?.rarity?.id);

const Gacha = ({ user = {}, users = [], appConfig = {}, onBack }) => {
    const [isSpinning, setIsSpinning] = useState(false);
    const [pulledCards, setPulledCards] = useState([]); 
    
    // ★修正: ローカルステートではなく、App.jsから渡ってくるリアルタイムなユーザー情報を直接使う
    const lastFreePullDate = user?.lastFreeGachaDate || '';
    const hasDoneFirstFree10 = user?.hasDoneFirstFree10 || false;

    const pointUnit = appConfig.pointUnitLabel || 'RB';
    const uid = user?.userId || user?.id;

    // ※ RARITIES / PROBABILITY は Cloud Functions 側に移した。
    //   カードの見た目（color / border）はサーバーが rarity に入れて返す。

    // ★ 排出対象の絞り込みとカード生成は Cloud Functions 側に移した。
    //   ここで行うと、コンソールから SSR を任意に出したり、
    //   好きなメンバーのカードを作ったりできてしまう。

    const handlePullGacha = async (type) => {
        if (!uid) return toast.error("ユーザーIDが見つかりません");

        const today = new Date().toLocaleDateString('ja-JP');
        const isFree = type === 'normal' && lastFreePullDate !== today;
        
        let cost = 0;
        let pullCount = 1;
        let gachaProbabilityType = 'normal';

        if (type === 'firstFree10') {
            cost = 0; 
            pullCount = 10; 
            gachaProbabilityType = 'normal';
        } else if (type === 'normal' && !isFree) {
            cost = 50;
        } else if (type === 'special') { 
            cost = 200; 
            gachaProbabilityType = 'special'; 
        } else if (type === 'special5') { 
            cost = 1000; 
            pullCount = 5; 
            gachaProbabilityType = 'special5'; 
        }

        if (cost > 0) {
            const confirmMsg = type === 'special5' ? `5連レアガチャを回しますか？\n(SSR確率アップ！)` : `ガチャを回しますか？`;
            const result = await Swal.fire({
                title: confirmMsg,
                html: `<b>${cost} ${pointUnit}</b> を消費します。<br>現在の所持: ${user.points || 0} ${pointUnit}`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '引く！',
                confirmButtonColor: '#e65100',
                cancelButtonText: 'やめる'
            });
            if (!result.isConfirmed) return;
        }

        setIsSpinning(true);
        setPulledCards([]);

        try {
            // ★ 抽選・カード生成・コスト徴収はすべてサーバーが行う
            const fns = getFunctions(getApp(), 'asia-northeast1');
            const res = await httpsCallable(fns, 'pullGacha')({ type });
            const newCards = res?.data?.cards || [];

            setTimeout(() => {
                setPulledCards(newCards);
                setIsSpinning(false);

                const hasSuperRare = newCards.some(c => ['SR','SSR','UR','LEGEND'].includes(c.rarity?.id));
                if (hasSuperRare) {
                    toast.success('✨ 激レアカード獲得！！ ✨', { duration: 3000, icon: '🎉' });
                }
            }, 2500);

        } catch (e) {
            setIsSpinning(false);
            console.error("Gacha Error:", e?.code || '', e?.message || e);
            // Functions の HttpsError は message に理由（残高不足・引き済み等）が入る
            Swal.fire('エラー', e?.message || 'ガチャの処理に失敗しました', 'error');
        }
    };

    const isTodayFree = lastFreePullDate !== new Date().toLocaleDateString('ja-JP');

    return (
        <div className="container" style={{ minHeight: '100vh', background: '#1a1a1a', paddingBottom: '50px', fontFamily: '"Helvetica Neue", Arial, sans-serif' }}>
            <Toaster position="top-center" />
            <style>
                {`
                @keyframes shake {
                    0% { transform: translate(1px, 1px) rotate(0deg); }
                    10% { transform: translate(-1px, -2px) rotate(-1deg); }
                    20% { transform: translate(-3px, 0px) rotate(1deg); }
                    30% { transform: translate(3px, 2px) rotate(0deg); }
                    40% { transform: translate(1px, -1px) rotate(1deg); }
                    50% { transform: translate(-1px, 2px) rotate(-1deg); }
                    60% { transform: translate(-3px, 1px) rotate(0deg); }
                    70% { transform: translate(3px, 1px) rotate(-1deg); }
                    80% { transform: translate(-1px, -1px) rotate(1deg); }
                    90% { transform: translate(1px, 2px) rotate(0deg); }
                    100% { transform: translate(1px, -2px) rotate(-1deg); }
                }
                @keyframes popIn {
                    0% { transform: scale(0.1); opacity: 0; }
                    60% { transform: scale(1.05); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .ssr-glow {
                    animation: rainbow-bg 2s linear infinite;
                    background-size: 400% 400%;
                }
                @keyframes rainbow-bg {
                    0%{background-position:0% 50%}
                    50%{background-position:100% 50%}
                    100%{background-position:0% 50%}
                }
                @keyframes pulse-btn {
                    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 127, 0.7); }
                    70% { transform: scale(1.02); box-shadow: 0 0 0 15px rgba(255, 0, 127, 0); }
                    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 0, 127, 0); }
                }
                `}
            </style>

            <div style={{ background: '#333', padding: '15px 20px', display: 'flex', alignItems: 'center', color: 'white', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', fontSize: '16px', cursor: 'pointer', padding: '0 10px 0 0' }}>← 戻る</button>
                <h2 style={{ margin: 0, fontSize: '18px', flex: 1, textAlign: 'center' }}>RBカードガチャ</h2>
                {/* ★ 排出率やレアリティ倍率を公開する */}
                <GameRulesButton appConfig={appConfig} label="📖 確率" />
            </div>

            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ 
                    background: 'radial-gradient(circle, #444 0%, #222 100%)', borderRadius: '20px', padding: '30px 15px',
                    marginBottom: '30px', border: '2px solid #555', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.8), 0 10px 20px rgba(0,0,0,0.5)'
                }}>
                    <div style={{ fontSize: '14px', color: '#ffd700', fontWeight: 'bold', marginBottom: '10px' }}>🔥 メンバーカードをゲットせよ！ 🔥</div>
                    
                    <div style={{ fontSize: '14px', color: '#fff', marginBottom: '20px' }}>
                        所持: <span style={{color: '#e65100', fontWeight: 'bold', fontSize: '20px'}}>{user?.points || 0}</span> {pointUnit}
                    </div>

                    <div style={{ 
                        fontSize: '80px', margin: '20px auto', display: 'inline-block',
                        animation: isSpinning ? 'shake 0.5s infinite' : 'none',
                        filter: isSpinning ? 'drop-shadow(0 0 20px #FFD700)' : 'drop-shadow(0 5px 10px rgba(0,0,0,0.5))',
                        transition: '0.3s'
                    }}>
                        {isSpinning ? '🌀' : '⚽️'}
                    </div>

                    <div style={{ display: 'grid', gap: '15px', marginTop: '20px' }}>
                        {/* ★修正: uid が存在し、かつ本当に hasDoneFirstFree10 が false の時だけボタンを出す */}
                        {(uid && !hasDoneFirstFree10) ? (
                            <button 
                                onClick={() => handlePullGacha('firstFree10')} disabled={isSpinning}
                                style={{ 
                                    background: 'linear-gradient(135deg, #ff007f, #ff8c00)', color: 'white', border: 'none', 
                                    padding: '20px 10px', fontSize: '18px', fontWeight: 'bold', borderRadius: '15px', 
                                    animation: isSpinning ? 'none' : 'pulse-btn 2s infinite',
                                    cursor: isSpinning ? 'not-allowed' : 'pointer' 
                                }}
                            >
                                🎁 初回限定！無料10連を引く！ 🎁<br/>
                                <span style={{fontSize:'12px', fontWeight:'normal'}}>※Sレア以上が1枚確定！一気にスタメンを作ろう！</span>
                            </button>
                        ) : (
                            <>
                                <button 
                                    onClick={() => handlePullGacha('normal')} disabled={isSpinning}
                                    style={{ background: isSpinning ? '#666' : (isTodayFree ? '#4caf50' : '#1976d2'), color: 'white', border: 'none', padding: '15px', fontSize: '16px', fontWeight: 'bold', borderRadius: '10px', boxShadow: isSpinning ? 'none' : '0 4px 10px rgba(0,0,0,0.3)', cursor: isSpinning ? 'not-allowed' : 'pointer' }}
                                >
                                    通常ガチャ<br/><span style={{fontSize:'12px'}}>{isTodayFree ? '本日1回 無料！' : `50 ${pointUnit}`}</span>
                                </button>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <button 
                                        onClick={() => handlePullGacha('special')} disabled={isSpinning}
                                        style={{ background: isSpinning ? '#666' : 'linear-gradient(135deg, #e65100, #ff9800)', color: 'white', border: 'none', padding: '15px 5px', fontSize: '14px', fontWeight: 'bold', borderRadius: '10px', boxShadow: isSpinning ? 'none' : '0 4px 10px rgba(0,0,0,0.3)', cursor: isSpinning ? 'not-allowed' : 'pointer' }}
                                    >
                                        レアガチャ<br/><span style={{fontSize:'12px'}}>200 {pointUnit}</span>
                                    </button>
                                    <button 
                                        onClick={() => handlePullGacha('special5')} disabled={isSpinning}
                                        style={{ background: isSpinning ? '#666' : 'linear-gradient(135deg, #d50000, #ff1744)', color: 'white', border: 'none', padding: '15px 5px', fontSize: '14px', fontWeight: 'bold', borderRadius: '10px', boxShadow: isSpinning ? 'none' : '0 4px 10px rgba(0,0,0,0.3)', cursor: isSpinning ? 'not-allowed' : 'pointer', position: 'relative' }}
                                    >
                                        <div style={{position:'absolute', top:'-10px', right:'-5px', background:'#ffea00', color:'#d50000', fontSize:'10px', padding:'2px 5px', borderRadius:'10px', border:'1px solid #d50000'}}>SSR確率UP</div>
                                        5連レアガチャ<br/><span style={{fontSize:'12px'}}>1000 {pointUnit}</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {pulledCards.length > 0 && !isSpinning && (
                    <div style={{ animation: 'popIn 0.5s both' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', marginBottom: '20px' }}>🎉 獲得カード 🎉</div>
                        
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '15px' }}>
                            {pulledCards.map((card, idx) => (
                                <div key={idx} className={isGlow(card) ? 'ssr-glow' : ''} style={{
                                    background: card.rarity.color, padding: isGlow(card) ? '4px' : '0', borderRadius: '16px', width: pulledCards.length > 1 ? '150px' : '240px', boxShadow: '0 10px 20px rgba(0,0,0,0.5)'
                                }}>
                                    <div style={{ background: '#222', borderRadius: '12px', border: isGlow(card) ? 'none' : card.rarity.border, padding: '10px', color: 'white', textAlign: 'left', backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.8) 100%)' }}>
                                        
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <span style={{ fontWeight: '900', fontSize: pulledCards.length > 1 ? '16px' : '24px', fontStyle: 'italic', background: card.rarity.color, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                                {card.rarity.id}
                                            </span>
                                            <span style={{ background: '#444', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>{card.position}</span>
                                        </div>

                                        <div style={{ width: pulledCards.length > 1 ? '60px' : '100px', height: pulledCards.length > 1 ? '60px' : '100px', margin: '0 auto 10px', borderRadius: '50%', background: '#555', border: `3px solid white`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            {card.pictureUrl ? <img src={card.pictureUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: pulledCards.length > 1 ? '30px' : '50px' }}>👤</span>}
                                        </div>

                                        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                                            <div style={{ fontSize: pulledCards.length > 1 ? '12px' : '18px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.name}</div>
                                            <div style={{ fontSize: '10px', color: '#aaa' }}>No. {card.number}</div>
                                            {!card.isPositionSet && <div style={{ fontSize: '9px', color: '#ff5252', marginTop: '2px' }}>⚠️能力半減(未設定)</div>}
                                        </div>

                                        <div style={{ background: 'rgba(0,0,0,0.5)', padding: '5px', borderRadius: '8px', fontSize: '9px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#ff5252' }}>OFF</span><span style={{ fontWeight: 'bold' }}>{card.stats.offense}</span></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#448aff' }}>DEF</span><span style={{ fontWeight: 'bold' }}>{card.stats.defense}</span></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#69f0ae' }}>STA</span><span style={{ fontWeight: 'bold' }}>{card.stats.stamina}</span></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#ffeb3b' }}>SPD</span><span style={{ fontWeight: 'bold' }}>{card.stats.speed}</span></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#e040fb' }}>TEC</span><span style={{ fontWeight: 'bold' }}>{card.stats.technique}</span></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ★スポンサー枠（ランダム1件）。掲載が無ければ何も表示されない。 */}
                        <div style={{ maxWidth: '400px', margin: '25px auto 0' }}>
                            <SponsorSpot variant="banner" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Gacha;